import React from "react";
import { PropTypes } from "prop-types";
import { mergeCss } from "mrb/common/utils";
import { PremiumBadge, Image } from "common/components";
import { useCanAccessContent, nakiRadioContentType } from "application/common/content-access";
import { observer } from "mobx-react";
import { findIndex, isNil } from "lodash";
import { podcastType } from "common/constants";

function CategoryGridCellTemplate(props) {
    if (!isNil(props.item.stationId)) {
        return <StationCell {...props} />;
    } else if (!isNil(props.item.podcastId)) {
        return <PodcastCell {...props} />;
    }
}

CategoryGridCellTemplate.propTypes = {
    onCellClick: PropTypes.func.isRequired,
    premiumSubscriptionStore: PropTypes.object.isRequired,
    goTo: PropTypes.func.isRequired,
    item: PropTypes.object.isRequired,
};

const StationCell = observer(function StationCell({ onCellClick, goTo, item, manageSelectedItemsViewStore }) {
    const contentAccessResult = useCanAccessContent(nakiRadioContentType.station, {
        isPremium: item.isPremium,
        unavailableInMobileApp: item.isVisibleInApp === false,
        unavailableInNakiTouch: item.isVisibleInEmbeddedApp === false,
    });
    const { isBulk, bulkViewStore } = manageSelectedItemsViewStore;

    function getBulkModeSelectIndicatorValue(
        item,
        canAccessStation,
        isAllSelected,
        selectedStationIds,
        deselectedStationIds
    ) {
        if (!canAccessStation) {
            return false;
        }
        const selectedIndex = findIndex(selectedStationIds, (selectedItemId) => selectedItemId === item.stationId);
        if (selectedIndex !== -1) {
            return true;
        }
        const deselectedIndex = findIndex(
            deselectedStationIds,
            (deselectedItemId) => deselectedItemId === item.stationId
        );
        if (deselectedIndex !== -1) {
            return false;
        }
        if (!isNil(isAllSelected)) {
            return isAllSelected;
        }
        return item.isTurnedOn;
    }

    function bulkSelectOrDeselect(item, canAccessStation, isAllSelected, selectedStationIds, deselectedStationIds) {
        const isSelected = getBulkModeSelectIndicatorValue(
            item,
            canAccessStation,
            isAllSelected,
            selectedStationIds,
            deselectedStationIds
        );
        if (isSelected) {
            bulkViewStore.deselectItem(item);
        } else {
            bulkViewStore.selectItem(item);
        }
    }

    return (
        <div
            className={mergeCss(
                "c-home__card c-home__card--stations",
                isBulk && (contentAccessResult.canAccess ? "c-home__card--secondary" : "c-home__card--tertiary")
            )}
        >
            <div
                className="c-home__card__media"
                onClick={() => {
                    isBulk
                        ? bulkSelectOrDeselect(
                              item,
                              contentAccessResult.canAccess,
                              bulkViewStore.bulkSelection.isAllSelected,
                              bulkViewStore.bulkSelection.selectedIds,
                              bulkViewStore.bulkSelection.deselectedIds
                          )
                        : onCellClick(item);
                }}
            >
                <div
                    className={
                        contentAccessResult.canAccess
                            ? mergeCss(
                                  "c-home__status c-home__status--secondary",
                                  isBulk
                                      ? getBulkModeSelectIndicatorValue(
                                            item,
                                            contentAccessResult.canAccess,
                                            bulkViewStore.bulkSelection.isAllSelected,
                                            bulkViewStore.bulkSelection.selectedIds,
                                            bulkViewStore.bulkSelection.deselectedIds
                                        )
                                          ? "c-home__status--enabled"
                                          : "c-home__status--disabled"
                                      : geStationActivityStatusIcon(item)
                              )
                            : null
                    }
                ></div>

                <div
                    className={mergeCss(
                        "c-home__card__img-wrapper jq-onboarding-turn-on-off",
                        contentAccessResult.canAccess
                            ? item.isTurnedOn
                                ? "status--disabled"
                                : "status--enabled"
                            : "status--off",
                        !contentAccessResult.canAccessOnSelectedDevice ? "u-opacity--4" : null
                    )}
                >
                    <Image src={item.coverImageUrl} />
                    <PremiumBadge
                        isPremium={item.isPremium}
                        isPremiumSubscriptionValid={contentAccessResult.canAccessPremium}
                    />
                </div>
            </div>

            <div className="c-home__card__title jq-onboarding-title" onClick={() => goTo(item)}>
                {item.title}
            </div>
        </div>
    );
});

const PodcastCell = observer(function PodcastCell({ onCellClick, goTo, item, manageSelectedItemsViewStore }) {
    const contentAccessResult = useCanAccessContent(nakiRadioContentType.podcast, {
        isPremium: item.isPremium,
        unavailableInMobileApp: item.isVisibleInApp === false,
        unavailableInNakiTouch: item.isVisibleInEmbeddedApp === false,
    });
    const { isBulk, bulkViewStore } = manageSelectedItemsViewStore;

    function getBulkModeSelectIndicatorValue(
        item,
        canAccessPodcast,
        isAllSelected,
        selectedPodcastIds,
        deselectedPodcastIds
    ) {
        if (!canAccessPodcast) {
            return false;
        }
        const selectedIndex = findIndex(selectedPodcastIds, (selectedItemId) => selectedItemId === item.podcastId);
        if (selectedIndex !== -1) {
            return true;
        }
        const deselectedIndex = findIndex(
            deselectedPodcastIds,
            (deselectedItemId) => deselectedItemId === item.podcastId
        );
        if (deselectedIndex !== -1) {
            return false;
        }
        if (!isNil(isAllSelected)) {
            return isAllSelected;
        }

        if (item.podcastType === podcastType.podcastGroup) {
            if (item.numberOfEnabledSubPodcasts === 0) {
                return false;
            } else if (item.numberOfSubPodcasts === item.numberOfEnabledSubPodcasts) {
                return true;
            } else if (item.numberOfSubPodcasts > item.numberOfEnabledSubPodcasts) {
                return false;
            }
        } else {
            return item.isTurnedOn;
        }
    }

    function bulkSelectOrDeselect(item, canAccessPodcast, isAllSelected, selectedPodcastIds, deselectedPodcastIds) {
        const isSelected = getBulkModeSelectIndicatorValue(
            item,
            canAccessPodcast,
            isAllSelected,
            selectedPodcastIds,
            deselectedPodcastIds
        );
        if (isSelected) {
            bulkViewStore.deselectItem(item);
        } else {
            bulkViewStore.selectItem(item);
        }
    }

    return (
        <div
            className={mergeCss(
                "c-home__card",
                isBulk && (contentAccessResult.canAccess ? "c-home__card--secondary" : "c-home__card--tertiary")
            )}
        >
            <div
                className="c-home__card__media"
                onClick={() => {
                    isBulk
                        ? bulkSelectOrDeselect(
                              item,
                              contentAccessResult.canAccess,
                              bulkViewStore.bulkSelection.isAllSelected,
                              bulkViewStore.bulkSelection.selectedIds,
                              bulkViewStore.bulkSelection.deselectedIds
                          )
                        : onCellClick(item);
                }}
            >
                <div
                    className={
                        contentAccessResult.canAccess
                            ? mergeCss(
                                  "c-home__status c-home__status--secondary",
                                  isBulk
                                      ? getBulkModeSelectIndicatorValue(
                                            item,
                                            contentAccessResult.canAccess,
                                            bulkViewStore.bulkSelection.isAllSelected,
                                            bulkViewStore.bulkSelection.selectedIds,
                                            bulkViewStore.bulkSelection.deselectedIds
                                        )
                                          ? "c-home__status--enabled"
                                          : "c-home__status--disabled"
                                      : getPodcastActivityStatusIcon(item)
                              )
                            : null
                    }
                ></div>

                <div
                    className={mergeCss(
                        "c-home__card__img-wrapper jq-onboarding-turn-on-off",
                        contentAccessResult.canAccess
                            ? item.isTurnedOn
                                ? "status--disabled"
                                : "status--enabled"
                            : "status--off",
                        !contentAccessResult.canAccessOnSelectedDevice ? "u-opacity--4" : null
                    )}
                >
                    <Image src={item.coverImageUrl} />
                    <PremiumBadge
                        isPremium={item.isPremium}
                        isPremiumSubscriptionValid={contentAccessResult.canAccessPremium}
                    />
                </div>
            </div>

            <div className="c-home__card__title jq-onboarding-title" onClick={() => goTo(item)}>
                {item.title}
            </div>
        </div>
    );
});

function getPodcastActivityStatusIcon(item) {
    if (item.podcastType === podcastType.podcastGroup) {
        if (item.numberOfEnabledSubPodcasts === 0) {
            return "c-home__status--disabled";
        } else if (item.numberOfSubPodcasts === item.numberOfEnabledSubPodcasts) {
            return "c-home__status--enabled";
        } else if (item.numberOfSubPodcasts > item.numberOfEnabledSubPodcasts) {
            return "c-home__status--mix";
        }
    } else {
        if (item.isTurnedOn) {
            return "c-home__status--enabled";
        } else {
            return "c-home__status--disabled";
        }
    }
}

function geStationActivityStatusIcon(item) {
    if (item.isTurnedOn) {
        return "c-home__status--enabled";
    } else {
        return "c-home__status--disabled";
    }
}

export default CategoryGridCellTemplate;
